import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  Send,
  MessageSquare,
  User,
  Search,
  Check,
  CheckCheck,
  Home,
  Wifi,
  WifiOff,
  Plus,
  X,
  Paperclip,
  File,
  Image,
  FileText,
  Download,
  Trash2,
  MoreVertical
} from "lucide-react";

const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(null); // Store typing user info
  const [wsConnected, setWsConnected] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // WebSocket URL - convert http(s) to ws(s)
  const WS_URL = API.replace(/^http/, 'ws').replace('/api', '');

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!user?.user_id || wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/ws/${user.user_id}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'new_message':
          // Add new message if in the same conversation
          if (selectedConv?.conversation_id === data.conversation_id) {
            setMessages(prev => [...prev, {
              message_id: data.message_id,
              sender_id: data.sender_id,
              content: data.content,
              created_at: data.created_at,
              read: false
            }]);
          }
          // Update conversations list
          fetchConversations();
          // Show notification if not in current conversation
          if (selectedConv?.conversation_id !== data.conversation_id) {
            toast.info(`New message from ${data.sender_name}`);
          }
          break;
          
        case 'typing_start':
          if (selectedConv?.conversation_id === data.conversation_id) {
            setIsTyping({ user_id: data.user_id, name: data.user_name });
          }
          break;
          
        case 'typing_stop':
          if (selectedConv?.conversation_id === data.conversation_id) {
            setIsTyping(null);
          }
          break;
          
        case 'message_read':
          if (selectedConv?.conversation_id === data.conversation_id) {
            setMessages(prev => prev.map(msg => ({ ...msg, read: true })));
          }
          break;
          
        case 'pong':
          // Keep-alive response
          break;
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      // Reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };

    wsRef.current = ws;
  }, [user?.user_id, WS_URL, selectedConv?.conversation_id]);

  // Send WebSocket message
  const sendWsMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!selectedConv) return;

    sendWsMessage({
      type: 'typing_start',
      conversation_id: selectedConv.conversation_id
    });

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendWsMessage({
        type: 'typing_stop',
        conversation_id: selectedConv.conversation_id
      });
    }, 2000);
  }, [selectedConv, sendWsMessage]);

  useEffect(() => {
    fetchConversations();
    connectWebSocket();

    // Keep-alive ping every 30 seconds
    const pingInterval = setInterval(() => {
      sendWsMessage({ type: 'ping' });
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [connectWebSocket, sendWsMessage]);

  // Handle URL params for direct messaging
  useEffect(() => {
    const toUserId = searchParams.get('to');
    const toUserName = searchParams.get('name');
    
    if (toUserId && toUserName) {
      // Check if conversation exists with this user
      const existingConv = conversations.find(c => c.other_user?.user_id === toUserId);
      
      if (existingConv) {
        setSelectedConv(existingConv);
      } else {
        // Create new conversation for this user
        const newConv = {
          conversation_id: `temp_${toUserId}`,
          other_user: {
            user_id: toUserId,
            name: decodeURIComponent(toUserName),
            picture: null
          },
          last_message: "",
          unread_count: 0,
          is_new: true
        };
        setConversations(prev => {
          // Don't add if already exists
          if (prev.some(c => c.other_user?.user_id === toUserId)) {
            return prev;
          }
          return [newConv, ...prev];
        });
        setSelectedConv(newConv);
      }
      // Clear URL params after handling
      navigate('/messages', { replace: true });
    }
  }, [searchParams, conversations, navigate]);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.conversation_id);
      // Mark as read via WebSocket
      sendWsMessage({
        type: 'message_read',
        conversation_id: selectedConv.conversation_id
      });
    }
  }, [selectedConv, sendWsMessage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/messages/conversations`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
        if (data.length > 0 && !selectedConv) {
          setSelectedConv(data[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      // Search all users for messaging
      const endpoint = `${API}/messages/search-users?name=${encodeURIComponent(query)}`;
      
      const response = await fetch(endpoint, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error("Failed to search users:", error);
    } finally {
      setSearching(false);
    }
  };

  const startConversationWith = async (recipient) => {
    setShowNewConversation(false);
    setSearchQuery("");
    setSearchResults([]);
    
    // Check if conversation already exists
    const existing = conversations.find(c => c.other_user?.user_id === recipient.user_id);
    if (existing) {
      setSelectedConv(existing);
      setShowConvList(false); // Switch to chat view on mobile
      // Fetch messages for existing conversation
      fetchMessages(existing.conversation_id);
      return;
    }
    
    // Create a new conversation object for UI
    const newConv = {
      conversation_id: `temp_${recipient.user_id}`,
      other_user: {
        user_id: recipient.user_id,
        name: recipient.name,
        picture: recipient.picture
      },
      last_message: "",
      unread_count: 0,
      is_new: true
    };
    
    // Clear messages for new conversation
    setMessages([]);
    setConversations(prev => [newConv, ...prev]);
    setSelectedConv(newConv);
    setShowConvList(false); // Switch to chat view on mobile
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(`${API}/messages/${conversationId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        fetchConversations();
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConv || sending) return;

    // Stop typing indicator
    sendWsMessage({
      type: 'typing_stop',
      conversation_id: selectedConv.conversation_id
    });

    setSending(true);
    try {
      const recipientId = selectedConv.other_user.user_id;
      const response = await fetch(`${API}/messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipient_id: recipientId,
          content: newMessage.trim(),
          attachments: attachments
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Add message to local state immediately
        setMessages(prev => [...prev, {
          message_id: data.message_id,
          sender_id: user.user_id,
          content: newMessage.trim(),
          attachments: attachments,
          created_at: new Date().toISOString(),
          read: false
        }]);
        setNewMessage("");
        setAttachments([]);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    handleTyping();
  };

  const deleteConversation = async (conversationId) => {
    if (!confirm("Are you sure you want to delete this conversation? This cannot be undone.")) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/messages/conversations/${conversationId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        // Remove from local state
        setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
        // If this was the selected conversation, clear it
        if (selectedConv?.conversation_id === conversationId) {
          setSelectedConv(null);
          setMessages([]);
          setShowConvList(true);
        }
        toast.success("Conversation deleted");
      } else {
        toast.error("Failed to delete conversation");
      }
    } catch (error) {
      toast.error("Failed to delete conversation");
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedAttachments = [];

    for (const file of files) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API}/messages/upload-attachment`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          uploadedAttachments.push(data);
        } else {
          const error = await response.json();
          toast.error(`Failed to upload ${file.name}: ${error.detail}`);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploadedAttachments.length > 0) {
      setAttachments(prev => [...prev, ...uploadedAttachments]);
    }
    setUploading(false);
    e.target.value = ''; // Reset file input
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getAttachmentIcon = (type) => {
    switch (type) {
      case 'image': return Image;
      case 'pdf': return FileText;
      case 'document': return FileText;
      default: return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const dashboardLink = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  // Mobile state - show conversation list or chat
  const [showConvList, setShowConvList] = useState(true);

  // When selecting a conversation on mobile, hide the list
  const handleSelectConv = (conv) => {
    setSelectedConv(conv);
    setShowConvList(false);
  };

  // Go back to conversation list on mobile
  const handleBackToList = () => {
    setShowConvList(true);
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar unreadCount={conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0)} />
      
      <main className="lg:ml-64 min-h-screen flex flex-col pt-16 lg:pt-0">
        <div className="flex flex-1 h-[calc(100vh-64px)] lg:h-screen">
          {/* Conversations List - Hidden on mobile when viewing a conversation */}
          <div className={`${showConvList ? 'flex' : 'hidden'} lg:flex w-full lg:w-80 border-r border-[#222] flex-col bg-obsidian-light`}>
            <div className="p-3 sm:p-4 border-b border-[#222] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-base sm:text-lg font-bold">MESSAGES</h2>
                {wsConnected ? (
                  <Wifi className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" title="Connected" />
                ) : (
                  <WifiOff className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" title="Connecting..." />
                )}
              </div>
              <Button
                onClick={() => setShowNewConversation(true)}
                size="sm"
                className="bg-crimson hover:bg-crimson-hover text-white h-9 px-3"
                data-testid="new-conversation-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="text-xs">New</span>
              </Button>
            </div>

            {/* New Conversation Modal */}
            <AnimatePresence>
              {showNewConversation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-b border-[#222] bg-obsidian-light"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-gray-400">New Conversation</span>
                      <button
                        onClick={() => {
                          setShowNewConversation(false);
                          setSearchQuery("");
                          setSearchResults([]);
                        }}
                        className="text-gray-500 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          searchUsers(e.target.value);
                        }}
                        placeholder="Search by name..."
                        className="pl-10 bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                        data-testid="search-users-input"
                      />
                    </div>
                    {searching && (
                      <div className="flex justify-center py-3">
                        <div className="w-4 h-4 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {searchResults.length > 0 && (
                      <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                        {searchResults.map((result) => (
                          <button
                            key={result.user_id}
                            onClick={() => startConversationWith(result)}
                            className="w-full p-2 text-left hover:bg-white/5 flex items-center gap-3 transition-colors"
                            data-testid={`user-result-${result.user_id}`}
                          >
                            <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {result.picture ? (
                                <img src={result.picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <User className="w-4 h-4 text-crimson" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="font-medium block truncate">{result.name}</span>
                              {result.headline && (
                                <span className="text-xs text-gray-500 truncate block">{result.headline}</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchQuery && !searching && searchResults.length === 0 && (
                      <p className="text-gray-500 text-sm text-center py-3">No users found</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
              </div>
            ) : conversations.length > 0 ? (
              <div className="flex-1 overflow-y-auto">
                {conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    className={`relative group w-full p-3 sm:p-4 text-left border-b border-[#1a1a1a] transition-colors ${
                      selectedConv?.conversation_id === conv.conversation_id
                        ? "bg-crimson/10 border-l-2 border-l-crimson"
                        : "hover:bg-white/5"
                    }`}
                  >
                    <button
                      onClick={() => handleSelectConv(conv)}
                      className="w-full text-left"
                      data-testid={`conversation-${conv.conversation_id}`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 pr-8">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                          {conv.other_user.picture ? (
                            <img src={conv.other_user.picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-bold truncate text-sm sm:text-base">{conv.other_user.name}</span>
                            {conv.unread_count > 0 && (
                              <span className="w-4 h-4 sm:w-5 sm:h-5 bg-crimson rounded-full text-[10px] sm:text-xs flex items-center justify-center text-white ml-1">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-500 text-xs sm:text-sm truncate">{conv.last_message}</p>
                        </div>
                      </div>
                    </button>
                    {/* Delete button - visible on hover (desktop) or always visible (mobile) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conv.conversation_id);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                      title="Delete conversation"
                      data-testid={`delete-conv-${conv.conversation_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                <MessageSquare className="w-12 h-12 text-gray-600 mb-4" />
                <p className="text-gray-400">No conversations yet</p>
              </div>
            )}
          </div>

          {/* Chat Area - Show on mobile when conversation is selected */}
          <div className={`${!showConvList || !selectedConv ? 'flex' : 'hidden'} lg:flex flex-1 flex-col`}>
            {selectedConv ? (
              <>
                {/* Chat Header */}
                <div className="p-3 sm:p-4 border-b border-[#222] flex items-center gap-2 sm:gap-3">
                  {/* Back button for mobile */}
                  <button 
                    onClick={handleBackToList}
                    className="lg:hidden p-1.5 hover:bg-white/10 rounded transition-colors"
                    data-testid="back-to-conversations"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-400" />
                  </button>
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                    {selectedConv.other_user.picture ? (
                      <img src={selectedConv.other_user.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-sm sm:text-base truncate">{selectedConv.other_user.name}</h3>
                    <AnimatePresence>
                      {isTyping && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-[10px] sm:text-xs text-crimson"
                        >
                          {isTyping.name} is typing...
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                  {/* Delete button in chat header */}
                  <button
                    onClick={() => deleteConversation(selectedConv.conversation_id)}
                    className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                    title="Delete conversation"
                    data-testid="delete-current-conv"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4 min-h-0">
                  {messages.map((msg, index) => {
                    const isOwn = msg.sender_id === user?.user_id;
                    const AttachIcon = msg.attachments?.length > 0 ? getAttachmentIcon(msg.attachments[0].type) : null;
                    return (
                      <motion.div
                        key={msg.message_id || index}
                        className={`flex ${isOwn ? "justify-end" : ""}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <div className={`max-w-[85%] sm:max-w-[70%] p-3 sm:p-4 ${
                          isOwn 
                            ? "bg-crimson/20 border border-crimson/30" 
                            : "bg-obsidian-light border border-[#222]"
                        }`}>
                          {msg.content && (
                            <p className="text-gray-200 text-sm sm:text-base whitespace-pre-wrap">{msg.content}</p>
                          )}
                          
                          {/* Attachments */}
                          {msg.attachments?.length > 0 && (
                            <div className={`space-y-2 ${msg.content ? 'mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-[#333]' : ''}`}>
                              {msg.attachments.map((att, attIndex) => {
                                const AttIcon = getAttachmentIcon(att.type);
                                return (
                                  <div key={attIndex}>
                                    {att.type === 'image' ? (
                                      <a href={`${API.replace('/api', '')}${att.url}`} target="_blank" rel="noopener noreferrer">
                                        <img 
                                          src={`${API.replace('/api', '')}${att.url}`} 
                                          alt={att.name}
                                          className="max-w-full max-h-36 sm:max-h-48 rounded cursor-pointer hover:opacity-80"
                                        />
                                      </a>
                                    ) : (
                                      <a 
                                        href={`${API.replace('/api', '')}${att.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-obsidian border border-[#333] hover:border-crimson/50 transition-colors"
                                      >
                                        <AttIcon className="w-6 h-6 sm:w-8 sm:h-8 text-crimson flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-white text-sm truncate">{att.name}</p>
                                          <p className="text-gray-500 text-xs">{formatFileSize(att.size)}</p>
                                        </div>
                                        <Download className="w-4 h-4 text-gray-400" />
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-end gap-1 mt-2">
                            <span className="text-xs text-gray-500">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isOwn && (
                              msg.read 
                                ? <CheckCheck className="w-3 h-3 text-crimson" />
                                : <Check className="w-3 h-3 text-gray-500" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                  
                  {/* Typing indicator in chat */}
                  <AnimatePresence>
                    {isTyping && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex items-center gap-2 text-gray-500"
                      >
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-crimson rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-crimson rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-crimson rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-sm">{isTyping.name} is typing...</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="px-4 py-2 border-t border-[#222] bg-obsidian-light">
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((att, index) => {
                        const AttIcon = getAttachmentIcon(att.type);
                        return (
                          <div 
                            key={index}
                            className="flex items-center gap-2 px-3 py-2 bg-obsidian border border-[#333] text-sm"
                          >
                            <AttIcon className="w-4 h-4 text-crimson" />
                            <span className="text-gray-300 max-w-[150px] truncate">{att.name}</span>
                            <button 
                              onClick={() => removeAttachment(index)}
                              className="text-gray-500 hover:text-red-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Input */}
                <div className="p-3 sm:p-4 border-t border-[#222] flex-shrink-0 mb-16 sm:mb-0">
                  <div className="flex gap-2 sm:gap-3">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.webp"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      variant="outline"
                      className="border-[#333] text-gray-400 hover:text-white px-2 sm:px-3 flex-shrink-0"
                      title="Attach files"
                      data-testid="attach-btn"
                    >
                      {uploading ? (
                        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Paperclip className="w-5 h-5" />
                      )}
                    </Button>
                    <Input
                      value={newMessage}
                      onChange={handleInputChange}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                      placeholder="Type a message..."
                      className="bg-obsidian-light border-[#333] text-white placeholder:text-gray-600 flex-1 text-sm sm:text-base"
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={(!newMessage.trim() && attachments.length === 0) || sending}
                      className="bg-crimson hover:bg-crimson-hover text-white px-3 sm:px-6 flex-shrink-0"
                      data-testid="send-btn"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                  {user?.role === "recruiter" && (
                    <p className="text-xs text-gray-500 mt-2">
                      Outreach credits: {user.outreach_credits || 0} remaining
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center">
                <MessageSquare className="w-16 h-16 text-gray-600 mb-4" />
                <p className="text-gray-400">Select a conversation to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Messages;
